import React, { useState, useEffect, useMemo } from 'react';
import { View, ScrollView } from 'react-native';
import { interpolateBlues, interpolateGnBu, schemeCategory10 } from 'd3-scale-chromatic';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import CustomDT from '../common/controls/customDataTable';
import PieChartComp from '../common/charts/piChart-builder';
import { findImmediateParent, getAllItems, getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { scaleOrdinal } from 'd3-scale';
import { colorType3, colorType5, logStyles } from './LogStyles';
import { Loader } from '../../Common/Loader';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import { useNav } from '../../../providers/NavProvider';
import LogKpiRow from './LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';

interface Props {
  siteId: string;
  logTitle: string;
}

export default function WaterLogScreen(props: Props) {
  const [LogData, setLogData] = useState<null | any>(null);
  const [LogColumns, setLogColumns] = useState<null | any>(null);
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();
  const { sitesResp } = useNav();

  useEffect(() => {
    async function fetchData() {
      try {
        const packageSite = sitesResp && findImmediateParent(sitesResp, props.siteId);
        if (packageSite && packageSite.id) {
          const logTitle = props.logTitle;
          const accessToken = await getAccessToken();
          const json = await getAllItems(logTitle, accessToken, clientAPIURL, props.siteId, undefined, packageSite.id);
          setLogData(json.items);
          setLogColumns(json.columns);
        }
      } catch (error) {
        console.error(error);
      }
    }
    sitesResp && fetchData();
  }, [sitesResp]);

  return LogData && LogColumns
    ? <CreateWaterLogCharts setLogData={setLogData} LogData={LogData} LogColumns={LogColumns} siteId={props.siteId} logTitle={props.logTitle} clientAPIURL={clientAPIURL} />
    : <Loader />;
}

interface CreateWaterLogChartsProps {
  LogData: any;
  LogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
  setLogData: any;
}

const CreateWaterLogCharts: React.FC<CreateWaterLogChartsProps> = ({ LogData, LogColumns, siteId, logTitle, clientAPIURL }) => {
  const { getAccessToken } = useAuth();
  const { currentLeafObj } = useNav();
  const defaultTab = currentLeafObj.permissions && JSON.stringify(currentLeafObj.permissions).toLowerCase().includes('write') ? 'list' : 'chart';

  const ColumnsWithIDColumn = LogColumns && [
    ...LogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    },
  ];

  const updatedData = LogData;
  const [filteredData, setFilteredData] = useState<any[]>(updatedData ?? []);

  useEffect(() => {
    setFilteredData(updatedData ?? []);
  }, [updatedData]);

  async function handleDataUpdate(item: any) {
    if (!item.ID) return;
    try {
      const accessToken = await getAccessToken();
      const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
      const rawItem = result?.items?.[0];
      if (!rawItem) return;
      const formatDate = (v: any): string => {
        if (!v) return v;
        const adj = new Date(new Date(v).getTime() + 4 * 60 * 60000);
        const d = adj.getDate(), m = adj.getMonth() + 1, y = adj.getFullYear();
        return `${d < 10 ? '0' + d : d}/${m < 10 ? '0' + m : m}/${y}`;
      };
      const formatPeriod = (v: any): string | null => {
        if (!v) return null;
        const adj = new Date(new Date(v).getTime() + 4 * 60 * 60000);
        const m = adj.getMonth() + 1, y = adj.getFullYear();
        return `${m < 10 ? '0' + m : m}-${y}`;
      };
      const updatedItem = { ...rawItem, Date: formatDate(rawItem.Date), Period: formatPeriod(rawItem.Date) };
      setFilteredData(prev => [...prev.filter((o: any) => o.ID !== item.ID), updatedItem]);
    } catch (ex) {
      console.error(ex);
    }
  }

  const chartData = useMemo(() => {
    const colorScaleLine = scaleOrdinal(schemeCategory10);

    const uniquePeriod = [...new Set(filteredData.map((item: any) => item.Period))].filter(Boolean) as string[];
    const uniqueWaterTypes = [...new Set(filteredData.map((item: any) => item.WaterType))].filter(Boolean) as string[];
    const uniqueUseTypes = [...new Set(filteredData.map((item: any) => item.Use))].filter(Boolean) as string[];

    // PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string
    const sumByWaterType: Record<string, number> = {};
    filteredData.forEach((item: any) => {
      const k = item.WaterType;
      if (k != null && String(k) !== 'null' && String(k) !== 'undefined') {
        sumByWaterType[String(k)] = (sumByWaterType[String(k)] ?? 0) + (Number(item.Quantitym3) || 0);
      }
    });
    const byWaterTypePieData = Object.entries(sumByWaterType).map(([name, value], idx) => ({
      name,
      value: Math.round(value),
      color: colorType5(idx.toString()),
    }));

    const sumByUse: Record<string, number> = {};
    filteredData.forEach((item: any) => {
      const k = item.Use;
      if (k != null && String(k) !== 'null' && String(k) !== 'undefined') {
        sumByUse[String(k)] = (sumByUse[String(k)] ?? 0) + (Number(item.Quantitym3) || 0);
      }
    });
    const byUsePieData = Object.entries(sumByUse).map(([name, value], idx) => ({
      name,
      value: Math.round(value),
      color: colorType3(idx.toString()),
    }));

    // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
    const lineDataByWaterType = {
      labels: uniquePeriod,
      datasets: uniqueWaterTypes.map((waterType, idx) => ({
        data: uniquePeriod.map(period =>
          filteredData
            .filter((item: any) => item.Period === period && item.WaterType === waterType)
            .reduce((sum: number, item: any) => sum + (Number(item.Quantitym3) || 0), 0),
        ),
        strokeWidth: 2,
        color: (_opacity = 1) => colorScaleLine(idx.toString()),
      })),
    };
    const legendByWaterType = uniqueWaterTypes.map(name => ({ name }));

    const lineDataByUse = {
      labels: uniquePeriod,
      datasets: uniqueUseTypes.map((use, idx) => ({
        data: uniquePeriod.map(period =>
          filteredData
            .filter((item: any) => item.Period === period && item.Use === use)
            .reduce((sum: number, item: any) => sum + (Number(item.Quantitym3) || 0), 0),
        ),
        strokeWidth: 2,
        color: (_opacity = 1) => colorScaleLine(idx.toString()),
      })),
    };
    const legendByUse = uniqueUseTypes.map(name => ({ name }));

    // KPI
    const totalWaterM3 = Math.round(
      filteredData.reduce((sum: number, item: any) => sum + (Number(item.Quantitym3) || 0), 0),
    );
    const kpiItems = [
      { label: 'Entries',           value: filteredData.length,  color: '#4527A0' },
      { label: 'Total Water (m3)',  value: totalWaterM3,          color: '#00695C' },
      ...Object.entries(sumByWaterType).map(([name, value], idx) => ({
        label: name,
        value: Math.round(value),
        color: colorType5(idx.toString()),
        unit: 'm³',
      })),
      ...Object.entries(sumByUse).map(([name, value], idx) => ({
        label: name,
        value: Math.round(value),
        color: colorType3(idx.toString()),
        unit: 'm³',
      })),
    ];

    // Pivot table — use column choice lists for fixed column headers
    const waterTypeLabels = LogColumns.find((col: any) => col.columnName === 'WaterType')?.choiceChoices;
    const waterTypesTable: string[] = waterTypeLabels
      ? waterTypeLabels.split('#;#').map((v: string) => v.split('#;#')[0])
      : uniqueWaterTypes;
    const useTypeLabels = LogColumns.find((col: any) => col.columnName === 'Use')?.choiceChoices;
    const useTypesTable: string[] = useTypeLabels
      ? useTypeLabels.split('#;#').map((v: string) => v.split('#;#')[0])
      : uniqueUseTypes;

    const groupedWaterTable = filteredData.reduce((result: any, item: any) => {
      const { Period, WaterType, Quantitym3 } = item;
      if (!result[Period]) {
        result[Period] = { Period };
        waterTypesTable.forEach((wt: string) => { result[Period][wt] = 0; });
      }
      if (WaterType && result[Period][WaterType] !== undefined) {
        result[Period][WaterType] += Math.round(Number(Quantitym3) || 0);
      }
      return result;
    }, {});

    const transformedWaterTable = Object.values(groupedWaterTable).map((values: any) => {
      const sumByWT = waterTypesTable.reduce((sum: number, wt: string) => sum + (values[wt] || 0), 0);
      return { ...values, 'Total (Water Type)': sumByWT };
    });

    const groupedUseTable = filteredData.reduce((result: any, item: any) => {
      const { Period, Use, Quantitym3 } = item;
      if (!result[Period]) {
        result[Period] = { Period };
        useTypesTable.forEach((ut: string) => { result[Period][ut] = 0; });
      }
      if (Use && result[Period][Use] !== undefined) {
        result[Period][Use] += Math.round(Number(Quantitym3) || 0);
      }
      return result;
    }, {});

    const transformedUseTable = Object.values(groupedUseTable).map((values: any) => {
      const sumByUT = useTypesTable.reduce((sum: number, ut: string) => sum + (values[ut] || 0), 0);
      return { ...values, 'Total (Use Type)': sumByUT };
    });

    const combinedData: { [key: string]: any } = {};
    transformedWaterTable.forEach((item: any) => {
      const { Period, ...rest } = item;
      combinedData[Period] = { ...combinedData[Period], ...rest };
    });
    transformedUseTable.forEach((item: any) => {
      const { Period, ...rest } = item;
      combinedData[Period] = { ...combinedData[Period], ...rest };
    });

    const mergedData = Object.entries(combinedData).map(([Period, values]) => ({ Period, ...values }));
    const columnKeys = mergedData[0]
      ? Object.keys(mergedData[0]).map((key, index) => ({ ColumnName: key, Order: index }))
      : [];

    return {
      kpiItems,
      byWaterTypePieData, legendByWaterType, lineDataByWaterType,
      byUsePieData, legendByUse, lineDataByUse,
      mergedData, columnKeys,
    };
  }, [filteredData, LogColumns]);

  const {
    kpiItems,
    byWaterTypePieData, legendByWaterType, lineDataByWaterType,
    byUsePieData, legendByUse, lineDataByUse,
    mergedData, columnKeys,
  } = chartData;

  const kpiRow = (
    <View style={{ zIndex: 200 }}>
      <LogKpiRow items={kpiItems}>
        <LogDateRangeFilter data={updatedData} onFilterChange={setFilteredData} />
      </LogKpiRow>
    </View>
  );

  return (
    <View style={logStyles.logRoot}>
      <TabsProvider defaultIndex={defaultTab === 'list' ? 2 : 0}>
        <Tabs style={{ backgroundColor: '#fff', height: '100%' }} showLeadingSpace={true}>
          <TabScreen label="Charts" icon="chart-bar">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              {kpiRow}
              <View style={logStyles.container}>
                <View style={logStyles.card}>
                  {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                  <PieChartComp data={byWaterTypePieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - m3)'} ChartTitle={'Water Quantities / Type'} />
                </View>
                <View style={logStyles.card}>
                  {/* LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=fn */}
                  <LineChartScreen legend={legendByWaterType} data={lineDataByWaterType} label={lineDataByWaterType.labels} subTitle={'(Monthly - m3)'} ChartTitle={'Water Quantities / Type'} />
                </View>
                <View style={logStyles.card}>
                  <PieChartComp data={byUsePieData} colorPallate={interpolateGnBu} subTitle={'(Cumulative - m3)'} ChartTitle={'Water Quantities / Use'} />
                </View>
                <View style={logStyles.card}>
                  <LineChartScreen legend={legendByUse} data={lineDataByUse} label={lineDataByUse.labels} subTitle={'(Monthly - m3)'} ChartTitle={'Water Quantities / Use'} />
                </View>
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="Tables" icon="table">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={logStyles.container}>
                <View style={logStyles.cardThree}>
                  {mergedData && columnKeys.length > 0 && (
                    <CustomDT
                      data={mergedData}
                      columns={columnKeys}
                      siteId={siteId}
                      logTitle={logTitle}
                      tableId={'WaterByType'}
                      tableTitle={'Water Quantities (m3) - By Type'}
                    />
                  )}
                </View>
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="List" icon="database">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={logStyles.container}>
                <View style={logStyles.cardThree}>
                  {filteredData && ColumnsWithIDColumn && (
                    <PaperTableDynamic
                      data={filteredData}
                      columns={ColumnsWithIDColumn}
                      siteId={siteId}
                      logTitle={logTitle}
                      updateData={handleDataUpdate}
                      isConsolidatedTable={false}
                    />
                  )}
                </View>
              </View>
            </ScrollView>
          </TabScreen>
        </Tabs>
      </TabsProvider>
    </View>
  );
};
